import React, { Component, ReactNode } from "react";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";

interface Props {
  className?: string,
  firstChild: ReactNode,
  secondChild: ReactNode,
  secondChildTitle?: {
    title: string | ReactNode, // string of the title text to render
    buttons?: { icon: string, onClick: (e?) => void }[], // buttons to render on the top right
  },
  splitHorizontal?: boolean,
  firstChildInitialSize?: number | string, // CSS style
  noPadding?: boolean, // removes padding from second-child-content (useful for nested splitters)
}

interface State {
  firstChildSize: number,
}

class MSplitter extends Component<Props, State> {
  dragStart: number;
  sizeStart: number;
  firstChildRef: React.RefObject<HTMLDivElement>;

  constructor(props: Props) {
    super(props);

    this.setDragListener = this.setDragListener.bind(this);
    this.getTitleButtons = this.getTitleButtons.bind(this);

    this.dragStart = null;
    this.sizeStart = null;

    this.firstChildRef = React.createRef();

    this.state = { firstChildSize: null };
  }

  setDragListener(node) {
    if (node) {
      node.addEventListener("pointerdown", event => {
        if (event.button !== 0) return; //only capture left mouse click
        this.dragStart = this.props.splitHorizontal ? event.clientY : event.clientX;
        this.sizeStart = this.props.splitHorizontal ? this.firstChildRef.current.offsetHeight : this.firstChildRef.current.offsetWidth;

        event.preventDefault();
        node.setPointerCapture(event.pointerId);
        window.getSelection().removeAllRanges();
        document.body.style.cursor = this.props.splitHorizontal ? "ns-resize" : "ew-resize";
      });

      node.addEventListener("pointermove", event => {
        if (!this.dragStart) return;
        let newSize = this.sizeStart - this.dragStart + (this.props.splitHorizontal ? event.clientY : event.clientX);
        this.setState({ firstChildSize: newSize });
      });

      node.addEventListener("pointerup", event => {
        if (event.button !== 0) return; //only capture left mouse click
        document.body.style.cursor = "default";
        node.releasePointerCapture(event.pointerId);
        this.dragStart = null;
        this.sizeStart = null;
      });
    }
  }

  getTitleButtons() {
    return this.props.secondChildTitle.buttons &&
      this.props.secondChildTitle.buttons.map((b, i) => {
        //if full screen is double clicked the next page loads fully selected. So this code will clear the document selection after half a second to prevent that
        let onClick = b.onClick;
        if (b.icon === "fullscreen") onClick = e => {
          setTimeout(() => document.getSelection().removeAllRanges(), 500);
          b.onClick(e);
        };

        return <IconButton key={`${b.icon}-${i}`} onClick={onClick}><Icon>{b.icon}</Icon></IconButton>;
      });
  }

  render() {
    let { firstChild, secondChild, secondChildTitle, splitHorizontal, firstChildInitialSize, className, noPadding } = this.props;

    let sizeStyle;
    let size = secondChild
      ? this.state.firstChildSize || (firstChildInitialSize ? firstChildInitialSize : "50%")
      : "100%";
    if (splitHorizontal) sizeStyle = { height: size };
    else sizeStyle = { width: size };

    return (
      <div className={`msplitter ${splitHorizontal ? "split-horizontal" : "split-vertical"} ${noPadding ? "no-padding" : ""} ${className || ""}`}>
        {firstChild &&
          <div ref={this.firstChildRef} className="first-child" style={sizeStyle}>
            {firstChild}
          </div>}
        {(firstChild && secondChild) &&
          <div className="resize-bar" ref={this.setDragListener} />}
        {secondChild &&
          <div className="second-child">
            {secondChildTitle &&
              <Card className="second-child-title">
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{secondChildTitle.title}</Typography>
                  <div>{this.getTitleButtons()}</div>
                </CardContent>
              </Card>
            }
            <div className="second-child-content">
              {secondChild}
            </div>
          </div>}
      </div>);
  }
}

export default MSplitter;